import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { Loader2, AlertTriangle, Coins, CheckCircle2 } from "lucide-react";
import Sidebar from "../components/Sidebar";
import Topbar from "../components/Topbar";
import SuperAdminOnlyNote from "../components/SuperAdminOnlyNote";
import api from "../services/api";
import { isSuperAdmin } from "../utils/role";
import { formatLagosDateTime } from "../utils/tripLog";

// All data comes from the backend (the "bucket"). Nothing is hardcoded.

const naira = (kobo) =>
  "₦" +
  Number((kobo || 0) / 100).toLocaleString("en-NG", {
    maximumFractionDigits: 0,
  });

const formatDate = (d) =>
  formatLagosDateTime(d, "en-NG", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

/**
 * Riders owed a milestone reward, and the redemption ledger behind it.
 *
 * This screen exists because the payout logic used to only fire when a rider's
 * settled-ride count landed EXACTLY on a multiple of the target — step past it
 * and the reward was skipped for good, with nothing recording the debt. The
 * audit below recomputes what each rider EARNED (from settled commissions)
 * against what they were actually PAID (from redemptions), so the gap is
 * visible instead of invisible.
 */
const RiderRewards = () => {
  const [audit, setAudit] = useState(null);
  const [redemptions, setRedemptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [settling, setSettling] = useState(false);
  const [tab, setTab] = useState("owed");
  const canEdit = isSuperAdmin();

  const load = async () => {
    setLoading(true);
    try {
      const [auditRes, redemptionRes] = await Promise.all([
        api.get("/v1/admin/promotions/rider-milestones/audit"),
        api.get("/v1/admin/promotions/redemptions?limit=200"),
      ]);
      setAudit(auditRes.data || null);
      setRedemptions(
        Array.isArray(redemptionRes.data)
          ? redemptionRes.data
          : redemptionRes.data?.data || []
      );
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Failed to load rider rewards"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const settle = async () => {
    if (!canEdit) return;
    const owed = audit?.totals?.milestonesOwed || 0;
    if (!owed) return;
    // Real money leaves the promotion budget here, so make the amount explicit
    // before it happens rather than after.
    const ok = window.confirm(
      `Pay ${owed} outstanding milestone reward(s) to ${audit.totals.ridersOwed} rider(s)?\n\n` +
        `Total: ${naira(audit.totals.owedKobo)}\n\n` +
        `This credits their wallets immediately. It is safe to re-run — each ` +
        `payout is unique, so nobody can be paid twice.`
    );
    if (!ok) return;

    setSettling(true);
    try {
      const { data } = await api.post(
        "/v1/admin/promotions/rider-milestones/settle"
      );
      toast.success(
        `Paid ${data?.paid ?? 0} milestone(s) — ${naira(data?.paidKobo || 0)}`
      );
      await load();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to settle rewards");
    } finally {
      setSettling(false);
    }
  };

  const totals = audit?.totals;
  const ridersOwed = audit?.ridersOwed || [];

  return (
    <div className="flex min-h-screen bg-gray-50 dark:bg-gray-900">
      <Sidebar />
      <div className="flex-1 flex flex-col w-full lg:pl-64">
        <Topbar />
        <main className="flex-1 overflow-y-auto p-6">
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Rider Rewards
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Milestone rewards earned from completed rides, and what has actually been paid.
            </p>
          </div>
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            </div>
          ) : (
            <>
              {/* Summary */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                    Riders owed
                  </p>
                  <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                    {totals?.ridersOwed ?? 0}
                  </p>
                </div>
                <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                    Milestones outstanding
                  </p>
                  <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                    {totals?.milestonesOwed ?? 0}
                  </p>
                </div>
                <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                    Total owed
                  </p>
                  <p className="text-2xl font-semibold text-amber-600">
                    {naira(totals?.owedKobo)}
                  </p>
                </div>
              </div>

              {/* Promo state + settle */}
              <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700 flex flex-wrap items-center justify-between gap-3">
                <div className="text-sm text-gray-600 dark:text-gray-300">
                  <span className="font-medium">
                    {audit?.target ?? 5} rides
                  </span>{" "}
                  per milestone · reward{" "}
                  <span className="font-medium">{naira(audit?.rewardKobo)}</span>
                  {audit?.promotionActive === false && (
                    <span className="ml-2 inline-flex items-center gap-1 text-amber-700 bg-amber-100 px-2 py-0.5 rounded text-xs">
                      <AlertTriangle className="w-3 h-3" />
                      Promotion is inactive — new milestones are not being paid
                    </span>
                  )}
                </div>
                {canEdit ? (
                  <button
                    onClick={settle}
                    disabled={settling || !totals?.milestonesOwed}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-amber-500 text-white text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed hover:bg-amber-600"
                  >
                    {settling ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Coins className="w-4 h-4" />
                    )}
                    Pay all outstanding
                  </button>
                ) : (
                  <SuperAdminOnlyNote />
                )}
              </div>

              {/* Tabs */}
              <div className="flex gap-2">
                {[
                  { key: "owed", label: `Owed (${ridersOwed.length})` },
                  { key: "ledger", label: `Redemptions (${redemptions.length})` },
                ].map((t) => (
                  <button
                    key={t.key}
                    onClick={() => setTab(t.key)}
                    className={`px-3 py-1.5 rounded-lg text-sm ${
                      tab === t.key
                        ? "bg-gray-900 text-white dark:bg-white dark:text-gray-900"
                        : "bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700"
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>

              {tab === "owed" ? (
                <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-x-auto">
                  {ridersOwed.length === 0 ? (
                    <div className="p-10 text-center text-sm text-gray-500 dark:text-gray-400">
                      <CheckCircle2 className="w-6 h-6 mx-auto mb-2 text-green-500" />
                      Every rider has been paid every milestone they have earned.
                    </div>
                  ) : (
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 dark:bg-gray-900/40 text-left text-gray-500 dark:text-gray-400">
                        <tr>
                          <th className="px-4 py-3 font-medium">Rider</th>
                          <th className="px-4 py-3 font-medium">Zooma ID</th>
                          <th className="px-4 py-3 font-medium">Phone</th>
                          <th className="px-4 py-3 font-medium text-right">Rides</th>
                          <th className="px-4 py-3 font-medium text-right">Earned</th>
                          <th className="px-4 py-3 font-medium text-right">Paid</th>
                          <th className="px-4 py-3 font-medium text-right">Owed</th>
                          <th className="px-4 py-3 font-medium text-right">Amount</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                        {ridersOwed.map((r) => (
                          <tr key={r.riderId}>
                            <td className="px-4 py-3 text-gray-900 dark:text-white">
                              {r.name || "—"}
                            </td>
                            <td className="px-4 py-3 font-mono text-xs text-gray-600 dark:text-gray-300">
                              {r.zoomaId || r.riderCode || "—"}
                            </td>
                            <td className="px-4 py-3 text-gray-600 dark:text-gray-300">
                              {r.phone || "—"}
                            </td>
                            <td className="px-4 py-3 text-right text-gray-600 dark:text-gray-300">
                              {r.completedRides}
                            </td>
                            <td className="px-4 py-3 text-right text-gray-600 dark:text-gray-300">
                              {r.earnedMilestones}
                            </td>
                            <td className="px-4 py-3 text-right text-gray-600 dark:text-gray-300">
                              {r.paidMilestones}
                            </td>
                            <td className="px-4 py-3 text-right font-medium text-amber-600">
                              {r.owedMilestones}
                            </td>
                            <td className="px-4 py-3 text-right font-medium text-gray-900 dark:text-white">
                              {naira(r.owedKobo)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              ) : (
                <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-x-auto">
                  {redemptions.length === 0 ? (
                    <div className="p-10 text-center text-sm text-gray-500 dark:text-gray-400">
                      No redemptions recorded yet.
                    </div>
                  ) : (
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 dark:bg-gray-900/40 text-left text-gray-500 dark:text-gray-400">
                        <tr>
                          <th className="px-4 py-3 font-medium">When</th>
                          <th className="px-4 py-3 font-medium">User</th>
                          <th className="px-4 py-3 font-medium">Type</th>
                          <th className="px-4 py-3 font-medium">Reference</th>
                          <th className="px-4 py-3 font-medium text-right">Amount</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                        {redemptions.map((r) => (
                          <tr key={r._id}>
                            <td className="px-4 py-3 text-gray-600 dark:text-gray-300">
                              {formatDate(r.createdAt)}
                            </td>
                            <td className="px-4 py-3 text-gray-900 dark:text-white">
                              {r.userId?.name || "—"}
                              <span className="ml-2 font-mono text-xs text-gray-500">
                                {r.userId?.zoomaId || r.userId?.riderId || ""}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-gray-600 dark:text-gray-300">
                              {String(r.type || "").replace(/_/g, " ")}
                            </td>
                            <td className="px-4 py-3 font-mono text-xs text-gray-500 dark:text-gray-400">
                              {r.reference}
                            </td>
                            <td className="px-4 py-3 text-right text-gray-900 dark:text-white">
                              {naira(r.amountKobo)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </>
          )}
        </main>
      </div>
    </div>
  );
};

export default RiderRewards;
